export const BACKEND_HEADER = "x-disputewell-backend";
const BACKEND_COOKIE = "dw_backend";
export const BACKEND_POSTGRES = "postgres";
export const BACKEND_CONVEX = "convex";
const POSTGRES_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

export type Backend = typeof BACKEND_CONVEX | typeof BACKEND_POSTGRES;

/** Whether this build carries the Postgres backend (NEXT_PUBLIC_POSTGRES_BACKEND_ENABLED, inlined into browser code).
 *  Off, which is every deployment today, the tRPC provider never mounts and no reader calls a tRPC hook, so no page
 *  renders or reads anything of that backend. */
export function isEnabled(): boolean {
    return process.env.NEXT_PUBLIC_POSTGRES_BACKEND_ENABLED === "1";
}

export function resolveBackend(input: { enabled?: boolean; cookieValue?: string | null; headerValue?: string | null; }): Backend {
    if (input.enabled !== true) {
        return BACKEND_CONVEX;
    }

    if (input.headerValue === BACKEND_POSTGRES) {
        return BACKEND_POSTGRES;
    }

    if (input.cookieValue === BACKEND_POSTGRES) {
        return BACKEND_POSTGRES;
    }

    return BACKEND_CONVEX;
}

export function readBackendCookie(cookieHeader: string | null | undefined): string | null {
    if (!cookieHeader) {
        return null;
    }

    for (const part of cookieHeader.split(";")) {
        const separatorIndex = part.indexOf("=");
        if (separatorIndex < 0) {
            continue;
        }

        const name = part.slice(0, separatorIndex).trim();
        if (name !== BACKEND_COOKIE) {
            continue;
        }

        const value = part.slice(separatorIndex + 1).trim();
        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    }

    return null;
}

export function resolveBackendFromHeaders(headers: Pick<Headers, "get">): Backend {
    return resolveBackend({ enabled: isEnabled(), headerValue: headers.get(BACKEND_HEADER), cookieValue: readBackendCookie(headers.get("cookie")) });
}

export function resolveBackendFromRequest(request: Pick<Request, "headers">): Backend {
    return resolveBackendFromHeaders(request.headers);
}

export function isPostgresRequest(request: Pick<Request, "headers">): boolean {
    return resolveBackendFromRequest(request) === BACKEND_POSTGRES;
}

export function resolveBrowserBackend(): Backend {
    const cookieValue = typeof document === "undefined" ? null : readBackendCookie(document.cookie);

    return resolveBackend({ enabled: isEnabled(), cookieValue, headerValue: null });
}

export function isPostgresId(value: unknown): value is string {
    return typeof value === "string" && POSTGRES_ID_PATTERN.test(value);
}
